import { IconButton } from "@/components/form"
import Icon from "@/components/Icon"
import { useLoading } from "@/composition"
import { MarriageStageType } from "@/models"
import { faCheck, faRefresh, faXmark } from "@fortawesome/free-solid-svg-icons"
import { css } from "vite-css-in-js"
import { defineComponent, runWithMinimumDelay, type ReactiveComponent } from "vue-utils"
import { useEditingMarriageClaimState } from "../state"
import { getBooleanDisplayValue, isEmptyStringOrNull } from "./utils"

const formSectionStyling = css`
	display: grid;
	align-items: center;
	grid-template-columns: repeat(2, 1fr);
	gap: 0.5rem 2rem;

	h6 {
		margin-bottom: 0;
	}

	@media (width < 992px) {
		grid-template-columns: 1fr;
	}
`

const signatureBoxStyling = css`
	display: flex;
	flex-direction: column;
	gap: 0.5rem 2rem;

	div {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 1rem;
		color: #fff;
		border-radius: 1rem;
		padding: 0.5rem 0 0.5rem 0;
	}

	img {
		border: 1px solid black;
		background-color: #fff;
		border-radius: 1rem;
	}
`

const SignatureInformation: ReactiveComponent = () => {
	const state = useEditingMarriageClaimState()
	const { runAction } = useLoading()

	return () => (
		<div class={formSectionStyling}>
			<div class="d-flex gap-1">
				<h6>Claimant Agreed to Terms:</h6>
				<h6 class="fw-bold">{getBooleanDisplayValue(state.fullClaim.Signature.AgreeToTerms, "Unknown")}</h6>
			</div>
			<div class="d-flex gap-1">
				<h6>Partner Agreed to Terms:</h6>
				<h6 class="fw-bold">{getBooleanDisplayValue(state.fullClaim.PartnerSignature.AgreeToTerms, "Unknown")}</h6>
			</div>
			{state.fullClaim.Signature.SignatureSVG !== null && (
				<div class="d-flex flex-column gap-2">
					<div class="d-flex gap-1">
						<h6>Claimant IP Address:</h6>
						<h6 class="fw-bold">
							{isEmptyStringOrNull(state.getSignatureData.IPAddress) ? `Unknown` : state.getSignatureData.IPAddress}
						</h6>
					</div>
					<div class="d-flex gap-1">
						<h6>Claimant Signature Timestamp:</h6>
						<h6 class="fw-bold">
							{state.getSignatureData.Timestamp === null ? `Unknown` : state.getSignatureData.Timestamp.toGBString()}
						</h6>
					</div>
					<div class={signatureBoxStyling}>
						<h6>Claimant Signature:</h6>
						<div style={`background-color: ${state.getSignatureData.IsVerified ? `#198754` : `#dc3545`}`}>
							<h6>{state.getSignatureData.IsVerified ? `Verified` : `Unverified`}</h6>
							<Icon icon={state.getSignatureData.IsVerified ? faCheck : faXmark} />
						</div>
						<img src={state.fullClaim.Signature.SignatureSVG} alt="Claimant Signature" />
					</div>
					<IconButton
						class="mt-2 w-100"
						color="primary"
						icon={faRefresh}
						onClick={() =>
							void runAction(runWithMinimumDelay(state.refreshSignatureDetails(MarriageStageType.P2), 1000))
						}
					>
						Verify Claimant Signature
					</IconButton>
				</div>
			)}
			{state.fullClaim.PartnerSignature.SignatureSVG !== null && (
				<div class="d-flex flex-column gap-2">
					<div class="d-flex gap-1">
						<h6>Partner IP Address:</h6>
						<h6 class="fw-bold">
							{isEmptyStringOrNull(state.getPartnerSignatureData.IPAddress)
								? `Unknown`
								: state.getPartnerSignatureData.IPAddress}
						</h6>
					</div>
					<div class="d-flex gap-1">
						<h6>Partner Signature Timestamp:</h6>
						<h6 class="fw-bold">
							{state.getPartnerSignatureData.Timestamp === null
								? `Unknown`
								: state.getPartnerSignatureData.Timestamp.toGBString()}
						</h6>
					</div>
					<div class={signatureBoxStyling}>
						<h6>Partner Signature:</h6>
						<div style={`background-color: ${state.getPartnerSignatureData.IsVerified ? `#198754` : `#dc3545`}`}>
							<h6>{state.getPartnerSignatureData.IsVerified ? `Verified` : `Unverified`}</h6>
							<Icon icon={state.getPartnerSignatureData.IsVerified ? faCheck : faXmark} />
						</div>
						<img src={state.fullClaim.PartnerSignature.SignatureSVG} alt="Partner Signature" />
					</div>
					<IconButton
						class="mt-2 w-100"
						color="primary"
						icon={faRefresh}
						onClick={() =>
							void runAction(runWithMinimumDelay(state.refreshSignatureDetails(MarriageStageType.P2), 1000))
						}
					>
						Verify Partner Signature
					</IconButton>
				</div>
			)}
		</div>
	)
}

export default defineComponent(SignatureInformation)
